import React, { useState } from "react";
import { Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, getApiErrorMessage } from "../../api/client";
import { useAuth } from "../../contexts/AuthContext";
import { fmtCurrency } from "../../lib/display-labels";
import { regionName } from "../ProvinceSelect";
import LoadRatesFields from "./LoadRatesFields";
import {
  BILLING, draftFromLoad, invalidField, isBillingType, rateKindLabel, ratesEditFields, readRefusals, sourceLabel,
  type RateCardRefusal, type RatesDraft,
} from "../../lib/load-rates";

/**
 * A load's billing and rates on its BOL page, and where each came from
 * (api#950). Editable until the BOL is signed; after that the API refuses any
 * rate or quantity change with LOAD_RATES_LOCKED, and the panel says so
 * instead of offering an edit.
 *
 * Every figure is the load's own column as the API sent it. A kind the rate
 * card had no row for, and that nobody typed, has no value and no source:
 * it reads "—" and says it is not on the card, never $0.
 */
const num = (v: unknown) => (v === null || v === undefined || v === "" ? null : v);

export default function LoadRatesPanel({
  load,
  initialRefusals = [],
  onSaved,
}: {
  load: Record<string, any>;
  initialRefusals?: RateCardRefusal[];
  onSaved: () => void;
}) {
  const { company } = useAuth();
  const currency = (company as { currency?: string } | null)?.currency ?? null;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<RatesDraft>(() => draftFromLoad(load));
  const [saving, setSaving] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [refusals, setRefusals] = useState<RateCardRefusal[]>(initialRefusals);

  const signed = !!load.signed_at;
  const cancelled = load.status === "cancelled";
  const billing = isBillingType(load.billing_type) ? BILLING[load.billing_type] : null;
  const money = (v: unknown) => fmtCurrency(num(v) as string | null, currency);
  const notOnCard = (value: unknown, source: unknown) =>
    num(value) === null && !source ? "Not on the rate card" : sourceLabel(source);

  const fuelValue =
    load.fuel_basis === "fixed" ? money(load.fuel_amount)
      : load.fuel_basis === "percent" && num(load.fuel_percent) !== null ? `${load.fuel_percent}%`
        : "—";
  const fuelHas = load.fuel_basis === "fixed" || load.fuel_basis === "percent" ? load.fuel_basis : null;

  const rows: { label: string; value: string; source?: string }[] = [
    { label: "Billing", value: billing ? billing.label : "—" },
    {
      label: billing ? billing.rateLabel : "Haul rate",
      value: billing ? money(load[billing.rateRead]) : "—",
      source: notOnCard(billing ? load[billing.rateRead] : null, load.haul_rate_source),
    },
    ...(billing?.qtyLabel && billing.qtyRead
      ? [{ label: billing.qtyLabel, value: num(load[billing.qtyRead]) === null ? "—" : String(load[billing.qtyRead]) }]
      : []),
    { label: "Wait rate (per hour)", value: money(load.wait_hourly_rate), source: notOnCard(load.wait_hourly_rate, load.wait_rate_source) },
    { label: "Free wait minutes", value: num(load.free_wait_minutes) === null ? "—" : String(load.free_wait_minutes) },
    { label: "Fuel surcharge", value: fuelValue, source: notOnCard(fuelHas, load.fuel_rate_source) },
    { label: "Province of the work", value: load.province_state ? regionName(load.province_state) : "—" },
  ];

  const save = async () => {
    const bad = invalidField(draft);
    if (bad) {
      setRefusal(`${bad} must be a number.`);
      return;
    }
    const body = ratesEditFields(draftFromLoad(load), draft);
    if (Object.keys(body).length === 0) {
      setEditing(false);
      return;
    }
    setSaving(true);
    setRefusal(null);
    try {
      const res = await api.trucking.loads.update(String(load.id), body);
      setRefusals(readRefusals(res.data));
      toast.success("Rates saved");
      setEditing(false);
      onSaved();
    } catch (err) {
      // LOAD_RATES_LOCKED, a fuel-shape 400, a province 400: as the API words it.
      setRefusal(getApiErrorMessage(err, "The rates were not saved."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      aria-labelledby="load-rates-title"
      data-testid="load-rates-panel"
      className="rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card"
    >
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-fieldiq-border px-4 py-4">
        <div>
          <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Billing</p>
          <h2 id="load-rates-title" className="mt-1 text-base font-extrabold tracking-tight text-fieldiq-text">Rates</h2>
        </div>
        {!signed && !cancelled && !editing && (
          <button
            type="button"
            onClick={() => { setDraft(draftFromLoad(load)); setRefusal(null); setEditing(true); }}
            className="inline-flex items-center gap-1.5 rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-3 py-2 text-sm font-medium text-fieldiq-text hover:bg-fieldiq-dark/50"
          >
            <Pencil className="size-4" aria-hidden="true" />
            Edit rates
          </button>
        )}
      </header>

      {refusals.length > 0 && (
        <ul data-testid="rate-card-refusals" className="space-y-1 border-b border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">
          {refusals.map((r, i) => (
            <li key={`${r.kind}-${i}`}>
              <span className="font-semibold">{rateKindLabel(r.kind)}:</span> {r.error}
            </li>
          ))}
        </ul>
      )}

      {editing ? (
        <div className="space-y-4 px-4 py-4">
          <LoadRatesFields draft={draft} onChange={setDraft} idPrefix="bol-rates" mode="edit" />
          {refusal && <p role="alert" data-testid="load-rates-refusal" className="text-sm font-medium text-fieldiq-text">{refusal}</p>}
          <div className="flex gap-2">
            <button type="button" onClick={save} disabled={saving} className="fi-action fi-action--standard fi-action--primary disabled:opacity-50">
              {saving && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
              Save rates
            </button>
            <button type="button" onClick={() => setEditing(false)} className="rounded-fi-control border border-fieldiq-border px-4 py-2.5 text-sm font-medium text-fieldiq-text hover:bg-fieldiq-dark/50">
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <dl className="grid gap-x-8 gap-y-3 px-4 py-4 sm:grid-cols-2 lg:grid-cols-3">
          {rows.map((r) => (
            <div key={r.label} data-testid={`rate-row-${r.label}`}>
              <dt className="text-[10px] font-bold uppercase tracking-wider text-fieldiq-muted">{r.label}</dt>
              <dd className="mt-0.5 text-sm text-fieldiq-text">
                <span className="font-mono">{r.value}</span>
                {r.source && <span className="ml-2 text-xs text-fieldiq-muted">{r.source}</span>}
              </dd>
            </div>
          ))}
        </dl>
      )}

      {signed && (
        <p className="border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-muted">Signed: rates and quantities are final.</p>
      )}
    </section>
  );
}
